/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceSupabase - A library for working with supabase made in Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.
*/

import { Observable, catchError, from, map, of } from "rxjs";
import type { Tables, TablesKeys } from "../supabase";
import {
	type CbRealtimeUpdate,
	ItemsGenericServiceBase,
} from "./items-generic-service-base";
import type { ItemsService } from "./items-service-interface";

export abstract class ItemsTableServiceBase<Entity extends Tables<TablesKeys>>
	extends ItemsGenericServiceBase<Entity>
	implements ItemsService<Entity>
{
	public realtimeUpdate = new Observable<CbRealtimeUpdate>((observer) => {
		this.onRealtimeFullyReady().subscribe(() => {
			this.supabaseClient
				.channel(this.TABLE)
				.on(
					"postgres_changes",
					{
						event: "*",
						schema: "public",
						table: this.TABLE,
					},
					(data) => {
						this.loggerService.logInfo(
							"REALTIME UPDATE DATA RECEIVED",
							this.TABLE,
							data,
						);

						switch (data.eventType) {
							case "INSERT":
								observer.next({
									type: "INSERT",
									table: this.TABLE,
									data: data.new,
								});
								break;
							case "UPDATE":
								observer.next({
									type: "UPDATE",
									table: this.TABLE,
									data: data.new,
								});
								break;
							case "DELETE":
								observer.next({
									type: "DELETE",
									table: this.TABLE,
									data: data.old,
								});
								break;
							default:
								break;
						}
					},
				)
				.subscribe((data) => {
					this.loggerService.logInfo("REALTIME UPDATE", this.TABLE, data);
				});
		});
	});

	public postOne(entity: Partial<Entity>): Observable<void> {
		if (this.hasTenant) {
			if ("tenant_id" in entity) {
				const tenantId = this.tenantId();
				if (!tenantId) {
					throw new Error("Tenant ID is not set");
				}

				entity.tenant_id = tenantId;
			}
		}
		return from(this.supabaseClient.from(this.TABLE).insert([entity])).pipe(
			catchError((error) => this.errorHandler(error)),
			map((resp) => {
				if (resp.error) throw resp.error;
				return void 0;
			}),
		);
	}

	public postDefault(entity?: Entity): Observable<void> {
		return this.postOne(entity ?? this.defaultItem());
	}

	public postMany(entities: Partial<Entity>[]): Observable<void> {
		if (this.hasTenant) {
			for (const entity of entities) {
				if ("tenant_id" in entity) {
					if (!this.tenantId()) {
						throw new Error("Tenant ID is not set");
					}

					entity.tenant_id = this.tenantId();
				}
			}
		}
		return from(this.supabaseClient.from(this.TABLE).insert(entities)).pipe(
			catchError((error) => this.errorHandler(error)),
			map((resp) => {
				if (resp.error) throw resp.error;
				return void 0;
			}),
		);
	}

	public putOne(entity: Partial<Entity> & { id: unknown }): Observable<void> {
		const tenantId = this.tenantId();

		if (this.hasTenant) {
			if ("tenant_id" in entity) {
				if (!tenantId) {
					throw new Error("Tenant ID is not set");
				}

				entity.tenant_id = tenantId;
			}
		}

		if (!("id" in entity)) throw new Error("Entity must have an ID");

		return from(
			this.supabaseClient
				.from(this.TABLE)
				.update(entity)
				.match({
					id: entity.id,
					...(this.hasTenant && tenantId
						? {
								tenant_id: tenantId,
							}
						: {}),
				}),
		).pipe(
			catchError((error) => this.errorHandler(error, true)),
			map((resp) => {
				if (resp.error) throw resp.error;
				return void 0;
			}),
		);
	}

	public upsert(entities: Entity[], onConflict?: string): Observable<void> {
		return this.putMany(entities, onConflict);
	}

	public putMany(
		entities: Partial<Entity>[],
		onConflict?: string,
	): Observable<void> {
		if (this.hasTenant) {
			for (const entity of entities) {
				if ("tenant_id" in entity) {
					const tenantId = this.tenantId();
					if (!tenantId) {
						throw new Error("Tenant ID is not set");
					}

					entity.tenant_id = tenantId;
				}
			}
		}

		return from(
			onConflict
				? this.supabaseClient.from(this.TABLE).upsert(entities, {
						onConflict: onConflict,
						ignoreDuplicates: true,
					})
				: this.supabaseClient.from(this.TABLE).upsert(entities),
		).pipe(
			catchError((error) => this.errorHandler(error)),
			map((resp) => {
				if (resp.error) throw resp.error;
				return void 0;
			}),
		);
	}

	public deleteOne(
		entity: Partial<Entity> & { id: unknown },
	): Observable<void> {
		return from(
			this.supabaseClient
				.from(this.TABLE)
				.delete()
				.match({
					id: entity.id,
					...(this.hasTenant
						? {
								tenant_id: this.tenantId(),
							}
						: {}),
				}),
		).pipe(
			catchError((error) => this.errorHandler(error)),
			map((response) => {
				if (response.error) throw response.error;
				return void 0;
			}),
		);
	}

	public deleteMany(
		entities: (Partial<Entity> & { id: string })[],
	): Observable<void> {
		if (!entities || entities.length === 0) {
			return of();
		}

		return from(
			this.supabaseClient.rpc("delete_many", {
				records: entities,
				table_name: this.TABLE,
			}),
		).pipe(
			catchError((error) => this.errorHandler(error)),
			map((resp) => {
				if (resp.error) throw resp.error;
				return void 0;
			}),
		);
	}
}
